/**
 * Customized to support the following:
 *  - Remove scripts from being rendered and now loaded using useLoadExternalScripts
 */

import { LinkWrap } from '@/components/blocks/Linkable';
import { ContentContext } from '@/data/context/content';
import { useSettings } from '@/data/Settings';
import { useStyleTheme } from '@/styles/theme';
import { mapAttributes } from '@/utils/contentParsing';
import { parseHTMLValidate } from '@/utils/parseHTMLValidate';
import parse, { domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import { FC, MouseEvent, useContext, useMemo } from 'react';

type HTMLAttributes = Record<string, string | ((e: MouseEvent) => void)>;

const ParsedElement: FC<any> = ({
	domChildren,
	SpecialComponent,
	Component,
	attribs,
	name,
	skipParsing,
}) => {
	const { additives, theme } = useStyleTheme();
	const { settings } = useSettings();
	const { onClick } = useContext(ContentContext) as {
		onClick?: (e: MouseEvent) => Promise<void>;
	};
	const mappedAttributes = useMemo(
		() => mapAttributes(attribs, additives, theme, settings, onClick),
		[additives, attribs, onClick, settings, theme]
	);
	const specialMappedAttributes = useMemo(
		() => (SpecialComponent ? {} : mappedAttributes),
		[SpecialComponent, mappedAttributes]
	);
	const options = useMemo(() => getParseHtmlOptions({ skipParsing }), [skipParsing]);

	return name === 'img' ? (
		<Component {...mappedAttributes} />
	) : (
		<LinkWrap {...specialMappedAttributes}>
			<Component {...mappedAttributes}>{domToReact(domChildren, options)}</Component>
		</LinkWrap>
	);
};

const getParseHtmlOptions = (options?: { skipParsing?: boolean }): HTMLReactParserOptions => ({
	replace: (domNode) => {
		if (domNode.type === 'script') {
			// Remove script elements from being inserted into the DOM.
			// eslint-disable-next-line react/jsx-no-useless-fragment
			return <></>;
		}

		if (domNode instanceof Element && domNode.name === 'a' && domNode.attribs?.onclick) {
			const onclickStr = domNode.attribs.onclick;
			delete domNode.attribs.onclick;
			(domNode.attribs as HTMLAttributes).onClick = (e: MouseEvent) => {
				try {
					const fn = new Function('event', onclickStr);
					fn(e);
				} catch (error) {
					console.error('Error executing onclick handler', error);
				}
			};
		}

		const { Component, SpecialComponent } = parseHTMLValidate(domNode, options) ?? {};
		if (!Component) return;
		const { children, attribs, name } = domNode as Element;

		return (
			<ParsedElement
				domChildren={children}
				SpecialComponent={SpecialComponent}
				Component={Component}
				attribs={attribs}
				name={name}
				skipParsing={options?.skipParsing}
			/>
		);
	},
});

export const parseHTML = (
	html: string,
	options?: {
		skipParsing?: boolean;
	}
) => parse(html, getParseHtmlOptions(options));
